// Link (DESIGN §3.17) und LinkProvider: @bar/ui kennt keinen Router. Die Plattform reicht ihren
// Router-Link über `LinkProvider` herein; ohne Provider rendert jeder Link ein normales <a>.
import { ArrowUpRight } from '@phosphor-icons/react';
import {
  type AnchorHTMLAttributes,
  type ComponentType,
  createContext,
  createElement,
  type ReactNode,
  type Ref,
  useContext,
} from 'react';
import { cn } from './cn';

export interface LinkComponentProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  ref?: Ref<HTMLAnchorElement>;
  /** Volle Navigation statt Router (andere Plattform, extern). */
  reload?: boolean;
  /** Router: aktuelle Seite ersetzen statt neuen Verlaufseintrag. */
  replace?: boolean;
}

export type LinkComponent = ComponentType<LinkComponentProps>;

function PlainAnchor({
  reload: _reload,
  replace: _replace,
  children,
  ...props
}: LinkComponentProps) {
  return <a {...props}>{children}</a>;
}

const LinkContext = createContext<LinkComponent>(PlainAnchor);

export function LinkProvider({
  component,
  children,
}: {
  component: LinkComponent;
  children: ReactNode;
}) {
  return <LinkContext.Provider value={component}>{children}</LinkContext.Provider>;
}

/** Router-Link der Plattform (unformatiert), z. B. für Navigationseinträge. */
export function useLinkComponent(): LinkComponent {
  return useContext(LinkContext);
}

/** Rendert den Router-Link der Plattform (unformatiert). */
export function RouterLink(props: LinkComponentProps) {
  return createElement(useContext(LinkContext), props);
}

/** true für absolute URLs (http, mailto) – diese laufen nie über den Router. */
export const isExternalHref = (href: string) => /^[a-z][a-z0-9+.-]*:/i.test(href);

export interface LinkProps extends LinkComponentProps {
  /** Externer Link: neuer Tab, `rel="noopener"`, Pfeil-Icon. */
  external?: boolean;
  /** Ohne Text-Stil (nur Fokus-Ring), z. B. um Karten. */
  unstyled?: boolean;
  children?: ReactNode;
}

/** Textlink in `accent-fg`, Unterstrich bei Hover/Fokus. */
export function Link({
  external,
  unstyled,
  className,
  children,
  href,
  reload,
  ...rest
}: LinkProps) {
  const outside = external ?? isExternalHref(href);
  const classes = cn(
    !unstyled &&
      'rounded-xs text-accent-fg underline-offset-2 hover:underline focus-visible:underline',
    className,
  );
  if (outside) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(classes, 'inline-flex items-center gap-1')}
        {...rest}
      >
        {children}
        <ArrowUpRight size={14} aria-hidden />
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
    );
  }
  return (
    <RouterLink href={href} reload={reload} className={classes} {...rest}>
      {children}
    </RouterLink>
  );
}
